import { gql, useQuery } from "@apollo/client";
import { datadogLogs } from "@datadog/browser-logs";
import i18next from "@lib/i18next";
import useCheckPC from "@repo/hooks/utils/useCheckPC";
import { searchQuery } from "@repo/shopify/src/queries/search-gql";
import type { IPredictiveSearchProduct, ISearchEdge, ISearchQuery } from "@repo/shopify/src/queries/search-gql";
import classNames from "classnames";
import qs from "query-string";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import Button from "@components/atoms/Button";
import PageTitle from "@components/atoms/PageTitle";
import Select from "@components/atoms/Select";
import Loading from "@components/molecules/Loading";

import LoadingIcon from "@atoms/LoadingIcon";

import ProductGrid from "@organisms/ProductGrid";

import useLayout from "@hooks/reducerHooks/useLayout";
import useMeta from "@hooks/reducerHooks/useMeta";

import { PRODUCT_TAG } from "@utils/archive-sale";

const SEARCH_SORT = [
  { name: i18next.t("collection.sort.relative"), value: "RELEVANCE,false" },
  { name: i18next.t("collection.sort.high_price"), value: "PRICE,true" },
  { name: i18next.t("collection.sort.low_price"), value: "PRICE,false" },
];

function Search({ isSaleShutDown }: { isSaleShutDown: boolean }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const query = qs.parse(location.search.replace("?", ""));
  const queryParameter = searchParams.get("q");
  const reverse = searchParams.get("reverse");
  const sort = searchParams.get("sort");

  const { t } = useTranslation();
  const { isPC } = useCheckPC();
  const { setMetaTag } = useMeta();
  const { showFooter } = useLayout();

  const loaderEl = useRef(null);

  useEffect(() => {
    setMetaTag({ title: `TUNE.KR - SEARCH RESULT FOR "${queryParameter}"` });
  }, [queryParameter]);

  const variables = {
    first: 24,
    types: "PRODUCT",
    query: queryParameter,
    sortKey: sort ? sort : "RELEVANCE",
    reverse: reverse ? (JSON.parse(reverse) as boolean) : false,
    prefix: "LAST",
    productFilters: [{ tag: PRODUCT_TAG }],
  };

  const sortName = useMemo(
    () => SEARCH_SORT.find((item) => item.value === `${sort},${reverse}`)?.name ?? SEARCH_SORT[0].name,
    [reverse, sort],
  );

  const { data, loading, fetchMore } = useQuery<ISearchQuery>(gql(searchQuery.query), {
    variables: { ...variables, productFiltersproductFilters: [{ tag: PRODUCT_TAG }] },
    notifyOnNetworkStatusChange: true,
    onError: (error) => {
      datadogLogs.logger.error("[SHOPIFY][Search] Failed to query", error);
      throw new Error(`[SHOPIFY][Search] Failed to query : ${JSON.stringify(error)}`);
    },
  });

  const hasNextPage = useMemo(() => data?.search?.pageInfo.hasNextPage || false, [data?.search?.pageInfo]);
  const endCursor = useMemo(() => data?.search?.pageInfo.endCursor || "", [data?.search?.pageInfo]);

  const products = useMemo(
    () => data?.search.edges?.filter(({ node }) => (node as ISearchEdge["node"]).hidden?.value === "false") || [],
    [data?.search],
  );

  useEffect(() => {
    showFooter(!hasNextPage);
  }, [hasNextPage]);

  /**
   * InterSectionObserver handler
   * 컬렉션 데이터 추가 쿼리
   * */
  const handleObserver = useCallback(
    async (entries: IntersectionObserverEntry[]) => {
      if (loading || !hasNextPage) return;

      const target = entries[0];

      if (target.isIntersecting) {
        await fetchMore({
          variables: { ...variables, after: endCursor },
          updateQuery: (prev, { fetchMoreResult }) => {
            if (!fetchMoreResult) return prev;

            return {
              search: {
                ...prev.search,
                edges: [...prev.search.edges, ...fetchMoreResult.search.edges],
                pageInfo: fetchMoreResult.search.pageInfo,
              },
            };
          },
        });
      }
    },
    [endCursor, hasNextPage, variables, loaderEl, loading],
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: "1000px 0px 0px 0px",
      threshold: 0.1,
    });

    if (loaderEl?.current) observer.observe(loaderEl.current);

    return () => {
      observer.disconnect();
    };
  }, [loaderEl, handleObserver]);

  return (
    <>
      {loading && !data ? (
        <Loading />
      ) : (
        <>
          {data && products.length === 0 ? (
            <section className="pc:col-span-12 pc:mt-5 pc:items-start col-span-6 flex flex-col items-center gap-5">
              <PageTitle
                className="mb-0"
                page={isPC ? "collection" : ""}
                title={isPC ? "Search Result" : `Search\nResult`}
              />
              <p>
                {`'${queryParameter}'`} {t("search.no_matches")}
              </p>
              <Button
                className="w-fit"
                onClick={() => {
                  navigate(-1);
                }}
              >
                Continue Shopping
              </Button>
            </section>
          ) : (
            <section className="pc:col-span-12 col-span-6 mt-5 flex flex-col gap-5">
              <div className="flex flex-col gap-5">
                <div className="flex justify-between">
                  <PageTitle className="mb-0" page="collection" title={isPC ? "Search Result" : `Search\nResult`} />
                  <div className="flex h-fit gap-1">
                    <span className="uppercase">Sort:</span>
                    <Select
                      onChange={(event) => {
                        const sort = event.target.value.split(",");
                        setSearchParams({ ...query, sort: sort[0], reverse: sort[1] });
                      }}
                      options={SEARCH_SORT}
                      selected={{ value: `${variables.sortKey},${variables.reverse}`, name: sortName }}
                      styleType="text"
                    />
                  </div>
                </div>
                <p>{`Search result for "${queryParameter}"`}</p>
              </div>
              <section className="relative">
                <div
                  className={classNames(
                    "absolute inset-0 bg-white transition-opacity duration-200",
                    loading ? "z-20 opacity-70" : "-z-10 opacity-0",
                  )}
                />
                <ProductGrid
                  isSaleShutDown={isSaleShutDown}
                  products={products as { node: IPredictiveSearchProduct }[]}
                />
              </section>
            </section>
          )}
        </>
      )}

      <div className="pc:col-span-12 col-span-6 w-full">
        <div id="loader" ref={loaderEl} />
        {hasNextPage ? <LoadingIcon className="flex h-24 items-center justify-center pb-5" type="spinner" /> : null}
      </div>
    </>
  );
}

export default Search;
